import { useEffect, useRef, useState } from "react";
import {
    Activity,
    BarChart3,
    BookOpen,
    CheckCircle2,
    Building2,
    ChevronDown,
    Database,
    FileText,
    GitBranch,
    Globe,
    Home,
    Layers,
    LifeBuoy,
    LogOut,
    Mail,
    MapPin,
    Newspaper,
    Search,
    Shield,
    Sparkles,
    Store,
    Tag,
    Ticket,
    Users,
    UserSearch,
    Zap,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPendingNewsCount } from "interfaces/restaurantNews";
import { getFranchiseReviewCounts } from "interfaces/events";
import { getSupportUnreadCount } from "interfaces/support";

const menuGroups = [
    {
        label: "Actions du jour",
        icon: null,
        items: [
            { label: "Validation des franchises", path: "/bo/franchise-review", icon: CheckCircle2 },
            { label: "Actualités à traiter", path: "/bo/news-articles", icon: Newspaper },
            { label: "Signalements", path: "/bo/signal-reports", icon: Shield },
        ],
    },
    {
        label: "Franchises",
        icon: null,
        items: [
            { label: "Franchises", path: "/bo/franchises", icon: Store },
            { label: "Affectation en masse", path: "/bo/todo-franchise", icon: GitBranch },
        ],
    },
    {
        label: "Terrain",
        icon: null,
        items: [
            { label: "Campagne hebdomadaire", path: "/bo/weekly-mail", icon: FileText },
            { label: "Appels de prospection", path: "/bo-commercial", icon: Users },
        ],
    },
    {
        label: "Établissements",
        icon: null,
        items: [
            { label: "Établissements à vérifier", path: "/bo/ghosts", icon: Store },
            { label: "Historique des annonces légales", path: "/bo/boddac-history", icon: Layers },
            { label: "Couverture géographique", path: "/bo/zone", icon: MapPin },
        ],
    },
    {
        label: "Dirigeants",
        icon: null,
        items: [
            { label: "Répertoire des personnes", path: "/bo/people", icon: Users },
            { label: "Enrichir les dirigeants", path: "/bo/founder-enrichment", icon: UserSearch },
            { label: "Corriger les identités", path: "/bo/identity-repair", icon: CheckCircle2 },
            { label: "Résoudre les doublons", path: "/bo/duplicate-people", icon: Users },
            { label: "Noms à contrôler", path: "/bo/weird-names", icon: UserSearch },
        ],
    },
    {
        label: "Enrichissement",
        icon: null,
        items: [
            { label: "Services", path: "/bo/services", icon: Layers },
            { label: "Qualification automatique", path: "/bo/auto-approve", icon: CheckCircle2 },
            { label: "KPI enrichissements", path: "/bo/enrichment-kpis", icon: BarChart3 },
            { label: "Import des lieux (OSM / DATAtourisme)", path: "/bo/import-lieux", icon: MapPin },
            { label: "Lieux non reliés (OSM / DATAtourisme)", path: "/bo/unmatched-places", icon: MapPin },
            { label: "Enrichissements validés", path: "/bo/enrichments/reviewed", icon: CheckCircle2 },
            { label: "Avancement des enrichissements", path: "/bo/enrichment-status", icon: BarChart3 },
            { label: "Anomalies de données", path: "/bo/status-monitoring", icon: Activity },
            { label: "Typologies clients", path: "/bo/client-typings", icon: UserSearch },
            { label: "Indicateurs de la base", path: "/bo/db-stats", icon: BarChart3 },
        ],
    },
    {
        label: "Utilisateurs",
        icon: null,
        items: [
            { label: "Vue d'ensemble", path: "/bo/users", icon: Home },
            { label: "Support", path: "/bo/support", icon: LifeBuoy },
            { label: "Centre d'aide", path: "/bo/help-center", icon: BookOpen },
            { label: "Activité des utilisateurs", path: "/bo/activity", icon: Activity },
            { label: "Historique des recherches", path: "/bo/search-logs", icon: Search },
            { label: "API publique", path: "/bo/public-api", icon: Globe },
            { label: "Clés API", path: "/bo/api-keys", icon: Shield },
            { label: "Prompts IA", path: "/bo/ai-prompts", icon: Sparkles },
            { label: "Journaux techniques", path: "/bo/query-logs", icon: FileText },
            { label: "Cache", path: "/bo/cache", icon: Database },
        ],
    },
    {
        label: "Étiquettes",
        icon: Tag,
        items: [
            { label: "Étiquettes à attribuer", path: "/bo/todo-tags", icon: Tag },
            { label: "Attribuer des étiquettes en masse", path: "/bo/bulk-tags", icon: Tag },
            { label: "Étiquettes non reconnues", path: "/bo/unknown-tags", icon: Tag },
            { label: "Étiquettes exclues", path: "/bo/banished-tags", icon: Tag },
        ],
    },
    {
        label: "Archive",
        icon: null,
        items: [
            { label: "Enrichissements à examiner", path: "/bo/enrichments", icon: Zap },
            { label: "Créer un signal et une entreprise", path: "/bo/create-company-signal", icon: Building2 },
            { label: "Signaux", path: "/bo/signals", icon: Zap },
            { label: "Événements", path: "/bo/events", icon: Activity },
            { label: "Entreprises", path: "/bo/companies", icon: Building2 },
            { label: "Envoi d'emails", path: "/bo/emails", icon: Mail },
            { label: "Importer un fichier CSV", path: "/bo/import-csv", icon: FileText },
            { label: "Liens de propriété", path: "/bo/ownership-tree", icon: GitBranch },
            { label: "Importer des établissements", path: "/bo/import-etablissements", icon: Building2 },
            { label: "Actualités commerciales", path: "/bo-commercial/news", icon: Newspaper },
            { label: "Contrôles AlimConfiance", path: "/bo/alim-confiance", icon: Shield },
        ],
    },
    // Last, so it sits rightmost. These two pages live here and nowhere else in the menu.
    {
        label: "Sales",
        icon: null,
        items: [
            { label: "Emails clients", path: "/bo/emailing", icon: Mail },
            { label: "Codes d'accès", path: "/bo/access-codes", icon: Ticket },
        ],
    },
];

export default function BackOfficeNavigation() {
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [pendingNewsCount, setPendingNewsCount] = useState(0);
    const [seenNewsCount, setSeenNewsCount] = useState(() => Number(localStorage.getItem("bo_news_seen_count") ?? 0));
    const [pendingFranchiseReviewCount, setPendingFranchiseReviewCount] = useState(0);
    const [supportUnreadCount, setSupportUnreadCount] = useState(0);
    const [seenFranchiseReviewCount, setSeenFranchiseReviewCount] = useState(
        () => Number(localStorage.getItem("bo_franchise_review_seen_count") ?? 0)
    );
    const menuRef = useRef<HTMLElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        getPendingNewsCount().then(setPendingNewsCount).catch(() => {});
        getFranchiseReviewCounts().then((counts) => setPendingFranchiseReviewCount(counts.pending)).catch(() => {});
    }, []);

    // Unread support tickets: refreshed on every page change, read state lives on the tickets themselves
    useEffect(() => {
        getSupportUnreadCount().then(setSupportUnreadCount).catch(() => {});
    }, [location.pathname]);

    useEffect(() => {
        setOpenMenu(null);
    }, [location.pathname]);

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (event.target instanceof Node && !menuRef.current?.contains(event.target)) {
                setOpenMenu(null);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpenMenu(null);
        };

        document.addEventListener("mousedown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("mousedown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, []);

    const menus = menuGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
            ...item,
            badge:
                item.path === "/bo/news-articles"
                    ? Math.max(0, pendingNewsCount - seenNewsCount)
                    : item.path === "/bo/franchise-review"
                    ? Math.max(0, pendingFranchiseReviewCount - seenFranchiseReviewCount)
                    : item.path === "/bo/support"
                    ? supportUnreadCount
                    : 0,
        })),
    }));
    const isActive = (path: string) =>
        path === "/bo" ? location.pathname === path : location.pathname === path || location.pathname.startsWith(`${path}/`);
    const goTo = (path: string) => {
        setOpenMenu(null);
        navigate(path);
    };
    const onItemClick = (path: string) => {
        if (path === "/bo/news-articles") {
            localStorage.setItem("bo_news_seen_count", String(pendingNewsCount));
            setSeenNewsCount(pendingNewsCount);
        } else if (path === "/bo/franchise-review") {
            localStorage.setItem("bo_franchise_review_seen_count", String(pendingFranchiseReviewCount));
            setSeenFranchiseReviewCount(pendingFranchiseReviewCount);
        }
        goTo(path);
    };

    return (
        <header className="relative z-50 w-full border-b border-[#e6e9f0] bg-white font-sans shadow-[0_2px_8px_rgba(20,32,64,0.06)]">
            <nav
                ref={menuRef}
                aria-label="Navigation du back office"
                className="mx-auto flex min-h-[68px] max-w-[1600px] flex-wrap items-center gap-1 px-4 md:px-6"
            >
                {menus.map((menu) => {
                    const isOpen = openMenu === menu.label;
                    const groupActive = menu.items.some((item) => isActive(item.path));
                    const GroupIcon = menu.icon;

                    return (
                        <div key={menu.label} className="relative shrink-0">
                            <button
                                type="button"
                                aria-haspopup="menu"
                                aria-expanded={isOpen}
                                onClick={() => setOpenMenu(isOpen ? null : menu.label)}
                                className={`inline-flex h-12 items-center gap-2 rounded-xl px-3 text-[14px] font-medium transition-colors ${
                                    isOpen || groupActive
                                        ? "bg-[#f5f7fb] text-[#17244a]"
                                        : "text-[#273354] hover:bg-[#f5f7fb]"
                                }`}
                            >
                                {GroupIcon && <GroupIcon className="h-4 w-4" />}
                                {menu.label}
                                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                            </button>

                            {isOpen && (
                                <div
                                    role="menu"
                                    className="absolute left-0 top-[calc(100%+8px)] z-50 min-w-[260px] rounded-2xl border border-[#e7eaf0] bg-white p-2 shadow-[0_12px_32px_rgba(20,32,64,0.14)]"
                                >
                                    {menu.items.map(({ label: itemLabel, path, icon: Icon, badge }) => (
                                        <button
                                            key={path}
                                            type="button"
                                            role="menuitem"
                                            onClick={() => onItemClick(path)}
                                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] transition-colors ${
                                                isActive(path)
                                                    ? "bg-[#f5f7fb] font-semibold text-[#17244a]"
                                                    : "text-[#273354] hover:bg-[#f5f7fb]"
                                            }`}
                                        >
                                            <Icon className="h-[18px] w-[18px] shrink-0" />
                                            {itemLabel}
                                            {!!badge && (
                                                <span className="ml-auto min-w-[20px] rounded-full bg-[#e34d59] px-1.5 py-0.5 text-center text-[10px] font-semibold leading-none text-white">
                                                    {badge > 99 ? "99+" : badge}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                <a
                    href="https://app.duckflow.io"
                    aria-label="Quitter le back office et retourner à l'application DuckFlow"
                    className="ml-auto inline-flex h-11 min-w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 text-[14px] font-medium text-[#273354] transition-colors hover:bg-[#f5f7fb]"
                >
                    <LogOut className="h-[18px] w-[18px]" />
                    <span className="inline">Quitter le BO</span>
                </a>
            </nav>
        </header>
    );
}
